import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X, Heart } from 'lucide-react';

const Header8 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template8' },
        { label: 'Shop', path: '/template8/shop' },
        { label: 'About', path: '/template8/about' },
        { label: 'Contact', path: '/template8/contact' },
        { label: 'Orders', path: '/template8/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=DM+Sans:wght@300;400;500;700&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? 'rgba(255,255,255,0.98)' : '#FFFFFF',
                    borderBottom: '1px solid #F3E8FF',
                    transition: 'all 0.3s ease',
                    boxShadow: scrolled ? '0 2px 20px rgba(190,24,93,0.06)' : 'none',
                }}
            >
                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '72px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template8')}
                        style={{
                            fontFamily: "'Libre Baskerville', serif",
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#1A1A1A',
                            cursor: 'pointer',
                            letterSpacing: '1px',
                            margin: 0,
                        }}
                    >
                        M<span style={{ color: '#BE185D' }}>E</span>HAK
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '32px',
                        }}
                        className="mehak-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    color: '#1A1A1A',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '4px 0',
                                    letterSpacing: '0.5px',
                                    transition: 'color 0.3s ease',
                                    position: 'relative',
                                }}
                                onMouseEnter={(e) => (e.target.style.color = '#BE185D')}
                                onMouseLeave={(e) => (e.target.style.color = '#1A1A1A')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                        <Search
                            size={20}
                            style={{ color: '#1A1A1A', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#BE185D')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1A1A1A')}
                        />
                        <Heart
                            size={20}
                            style={{ color: '#1A1A1A', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#BE185D')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1A1A1A')}
                        />
                        <User
                            size={20}
                            style={{ color: '#1A1A1A', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template8/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#BE185D')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1A1A1A')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template8/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#1A1A1A', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#BE185D')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#1A1A1A')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#BE185D',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        fontFamily: "'DM Sans', sans-serif",
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="mehak-mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            <Menu size={24} style={{ color: '#1A1A1A' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                    backgroundColor: '#FDF2F8',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}
            >
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <X size={28} style={{ color: '#BE185D' }} />
                </button>

                {/* Brand in mobile menu */}
                <h2
                    style={{
                        fontFamily: "'Libre Baskerville', serif",
                        fontSize: '28px',
                        fontWeight: 700,
                        color: '#BE185D',
                        marginBottom: '40px',
                        letterSpacing: '2px',
                    }}
                >
                    MEHAK
                </h2>

                <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: '18px',
                                fontWeight: 500,
                                color: '#1A1A1A',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '8px 0',
                                letterSpacing: '1px',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#BE185D')}
                            onMouseLeave={(e) => (e.target.style.color = '#1A1A1A')}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Responsive Styles */}
            <style>{`
                .mehak-desktop-nav {
                    display: flex !important;
                }
                .mehak-mobile-menu-btn {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    .mehak-desktop-nav {
                        display: none !important;
                    }
                    .mehak-mobile-menu-btn {
                        display: block !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Header8;
